import React, { useState, useEffect, useRef } from 'react';
import httpHelper from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';
import { Method_TYPE } from 'utils/http-helper';
import ReportHeader from './ReportHeader';
import { utils as XLSXUtils, writeFile } from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DataTable from 'react-data-table-component';
import { convertToIST } from 'utils/formtaDate';
import {
  useFilteredSortedData,
  ReportTableControls,
  ExportButtons,
} from './reportTableUtils';

const SEARCHABLE_FIELDS = ['samithi', 'district', 'registered'];

export default function SamithyWiseReg() {
  const [samithyWiseRegDetails, setSamithyWiseRegDetails] = useState([]);
  const printRef = useRef();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    getSamithyWiseRegDetails();
  }, []);

  const getSamithyWiseRegDetails = async () => {
    try {
      const data = await httpHelper(URL_CONSTANTS.GET_SAMITHYWISE_REGISTATION, Method_TYPE.GET);
      const district = await httpHelper(URL_CONSTANTS.GET_DISTRICT_REGISTATION, Method_TYPE.GET);
      const resultSet = data.map((tableData) => ({
        samithi: tableData?.samithi || '',
        district: district.find(dist => dist.id.toString() === (tableData?.district || '').toString())?.district || '',
        registered: tableData?.regCount || 0,
      }));
      setSamithyWiseRegDetails(resultSet);
    } catch (err) {
      console.error('Error fetching samithi details:', err);
      setSamithyWiseRegDetails([]);
    }
  };

  const filteredData = useFilteredSortedData(samithyWiseRegDetails, {
    searchTerm,
    searchableFields: SEARCHABLE_FIELDS,
    sortBy: null,
  });

  const getBatchInfo = () => {
    const batchData = JSON.parse(localStorage.getItem('currentBatch'));
    if (batchData) {
      return `${batchData.name}:${convertToIST(batchData.startDate)} - ${convertToIST(batchData.endDate)}`;
    }
    return '';
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Samithi wise Registration</title>');
    printWindow.document.write('<style>table, th, td { border: 1px solid black; border-collapse: collapse; padding: 8px; } th { background: #f2f2f2; }</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(`<h2>TamilNadu Prasanthi Seva - Samithi wise Registration</h2><p><strong>${getBatchInfo()}</strong></p>`);
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownloadXLS = () => {
    const exportData = filteredData.map((item, index) => ({
      'Sl.No': index + 1,
      Samithi: item.samithi,
      District: item.district,
      Registered: item.registered,
    }));
    const ws = XLSXUtils.json_to_sheet(exportData);
    const wb = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(wb, ws, 'Samithy Registration');
    writeFile(wb, 'samithy_wise_registration.xlsx');
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text('TamilNadu Prasanthi Seva - Samithi wise Registration', 14, 10);
    doc.text(`${getBatchInfo()}`, 14, 18);
    autoTable(doc, {
      startY: 20,
      head: [['Sl.No', 'Samithi', 'District', 'Registered']],
      body: filteredData.map((item, index) => [
        index + 1,
        item.samithi,
        item.district,
        item.registered,
      ]),
    });
    doc.save('Samithi_Registration_Report.pdf');
  };

  const columns = [
    {
      name: 'Sl.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
    },
    { name: 'Samithi', selector: row => row.samithi, sortable: true, cell: row => <div data-title="Samithi">{row.samithi}</div> },
    { name: 'District', selector: row => row.district, sortable: true, cell: row => <div data-title="District">{row.district}</div> },
    { name: 'Registered', selector: row => row.registered, sortable: true, cell: row => <div data-title="Registered">{row.registered}</div> },
  ];

  return (
    <>
      <ReportTableControls
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search samithi, district, registered count..."
        totalCount={samithyWiseRegDetails.length}
        filteredCount={filteredData.length}
        actions={
          <ExportButtons
            show={samithyWiseRegDetails.length > 0}
            onPrint={handlePrint}
            onPdf={handleDownloadPDF}
            onXls={handleDownloadXLS}
          />
        }
      />

      <div>
        <ReportHeader header='Samithy wise Registration' />
        <DataTable
          columns={columns}
          data={filteredData}
          defaultSortFieldId={2}
          fixedHeader
          fixedHeaderScrollHeight='500px'
          className='reports-table'
          noDataComponent={
            <div className="py-10 text-sm text-slate-500">
              {searchTerm.trim() ? 'No rows match your search' : 'No samithi registration data available'}
            </div>
          }
        />
      </div>

      <div ref={printRef} className="hidden">
        <table>
          <thead>
            <tr>
              <th>Sl.No</th>
              <th>Samithi</th>
              <th>District</th>
              <th>Registered</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.samithi}</td>
                <td>{item.district}</td>
                <td>{item.registered}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
